import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * Fabric Yardage (FYD) fractional quantity selectors.
 *
 * FYD fabric is stored/priced per raw ¼-yard unit but chosen by the shopper in fractional YARDS.
 * The checkout math depends on the RAW unit count being posted (per-¼-yard price × ¼-yard units),
 * and Horizon's native selector machinery is integer-only — so these components keep a hidden,
 * raw-unit `ref="quantityInput"` as the model that all the inherited logic (getValue/canAddToCart/
 * clamp/step/events) operates on, exactly as before, and layer a visible YARDS input on top:
 *
 *   yards shown = raw units / factor        (e.g. 4 raw ÷ 4 = 1 yd)
 *   raw posted  = round(yards × factor)
 *
 * Because the hidden input still carries `name="quantity"` (product) / `name="updates[]"` (cart)
 * and `onQuantityChange` still dispatches the raw value, the product form serialization, the cart
 * `QuantitySelectorUpdateEvent` → cart-change POST, and min/step clamping all stay raw-correct.
 *
 * See docs/plans/0004-fractional-quantity.md (§2, Option B).
 */

/**
 * Mixin adding the yards<->raw presentation layer to a QuantitySelectorComponent subclass.
 * @template {typeof QuantitySelectorComponent} T
 * @param {T} Base
 */
const Fractional = (Base) =>
  class extends Base {
    /** @returns {number} raw ¼-yard units per yard */
    get factor() {
      return Number(this.dataset.factor) || 4;
    }

    connectedCallback() {
      super.connectedCallback();
      // Normalize the server-rendered yards value (e.g. "1.0" -> "1") once mounted.
      this.syncYardsDisplay();
    }

    /**
     * Reflects the current raw value into the visible yards input.
     */
    syncYardsDisplay() {
      const yardsInput = this.refs.yardsInput;
      const { quantityInput } = this.refs;
      if (!yardsInput || !quantityInput) return;

      const raw = parseInt(quantityInput.value) || 0;
      const yards = raw / this.factor;
      const formatted = Number.isFinite(yards) ? String(Number(yards.toFixed(2))) : '';

      if (yardsInput.value !== formatted) yardsInput.value = formatted;
    }

    /**
     * The inherited machinery calls updateButtonStates after every raw change; piggyback on it to
     * keep the yards display in sync. (Cart subclass overrides updateButtonStates and calls
     * syncYardsDisplay itself.)
     */
    updateButtonStates() {
      super.updateButtonStates();
      this.syncYardsDisplay();
    }

    /**
     * Handles a yards edit (on blur of the visible input): convert to raw units, clamp/snap in the
     * raw domain, write the hidden input, and surface an error when the requested amount is below
     * the FYD minimum.
     * @param {Event} event
     */
    setYards(event) {
      if (!(event.target instanceof HTMLInputElement)) return;
      event.preventDefault();

      const { quantityInput } = this.refs;
      const factor = this.factor;
      const min = parseInt(quantityInput.min) || 1;
      const step = parseInt(quantityInput.step) || 1;
      const effectiveMax = this.getEffectiveMax();

      const yardsVal = parseFloat(event.target.value);
      const rawDesired = Math.round((Number.isFinite(yardsVal) ? yardsVal : 0) * factor);
      const belowMin = rawDesired < min;

      let raw = Math.min(effectiveMax ?? Infinity, Math.max(min, rawDesired));

      // Snap to the nearest valid raw increment.
      if ((raw - min) % step !== 0) {
        raw = min + Math.round((raw - min) / step) * step;
        raw = Math.min(effectiveMax ?? Infinity, Math.max(min, raw));
      }

      quantityInput.value = String(raw);
      this.onQuantityChange();
      this.updateButtonStates();

      if (belowMin) this.reportMinError();
    }

    /**
     * Selects the yards input contents on focus (mirrors the native selectInputValue).
     * @param {Event} event
     */
    selectYardsInput(event) {
      if (event.target instanceof HTMLInputElement) event.target.select();
    }

    /**
     * Surfaces the FYD minimum-quantity error on the visible yards input.
     */
    reportMinError() {
      const yardsInput = this.refs.yardsInput;
      if (!yardsInput || typeof yardsInput.reportValidity !== 'function') return;

      yardsInput.setCustomValidity(this.dataset.minError || '');
      yardsInput.reportValidity();
      setTimeout(() => yardsInput.setCustomValidity(''), 2000);
    }
  };

/**
 * Product-page FYD selector — inherits the product "how many can I add" max semantics.
 */
export class FractionalQuantitySelectorComponent extends Fractional(QuantitySelectorComponent) {}

/**
 * Cart-page FYD selector — replicates CartQuantitySelectorComponent's cart semantics (absolute max,
 * always client-managed buttons). The base cart class isn't exported, so its two overrides are
 * re-declared here.
 */
export class FractionalCartQuantitySelectorComponent extends Fractional(QuantitySelectorComponent) {
  /** Cart uses the absolute max (total allowed in cart), not max-minus-cart-quantity. */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max;
  }

  /** Cart buttons are always managed client-side, never server-disabled. */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    minusButton.disabled = value <= min;
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;

    this.syncYardsDisplay();
  }
}

if (!customElements.get('fractional-quantity-selector-component')) {
  customElements.define('fractional-quantity-selector-component', FractionalQuantitySelectorComponent);
}
if (!customElements.get('fractional-cart-quantity-selector-component')) {
  customElements.define('fractional-cart-quantity-selector-component', FractionalCartQuantitySelectorComponent);
}
